import { sparkToast } from '@spark';
import { data_from_server_post } from '@/shared/http/client';
import { isGenerativeTask } from '@/shared/domain/taskTypes';

/**
 * Persist a single example's response (labels / entities / relations) via
 * `POST /single-example/update-response`.
 *
 * Optional `options` (used by the NER annotate modal's editable example flow):
 * - `recordId`: locate the record by `_id` instead of by text, so saves keyed
 *   off a stale `request_example` still hit the right record.
 * - `requestExample`: override for the text key sent to the backend (the last
 *   text the server is known to have).
 * - `newRequestExample`: when set, the backend also renames the example text
 *   (and recomputes its metadata) in the same atomic update.
 */
export function updateRecordResponse(record, newResponse, dataset, isBenchmark, updateRecord, fetchValidationCounts, llm_usecase_id, activity_type, activity_comment, taskType, onDataOverviewRefresh, options = {}) {
    return new Promise((resolve, reject) => {
        // Check if all entities are validated (for NER tasks)
        const entities = newResponse?.entities || [];
        const relations = newResponse?.relations || [];

        // Only auto-set is_document_reviewed if there are entities and all are validated
        // For NER_WITH_RELATIONS, also check that all relations are validated
        const hasEntities = entities.length > 0;
        const allEntitiesValidated = hasEntities && entities.every(entity => entity.is_validated === true);
        const hasRelations = relations.length > 0;
        const allRelationsValidated = hasRelations ? relations.every(relation => relation.is_validated === true) : true;

        // If all entities (and relations if they exist) are validated, automatically set is_document_reviewed to true
        const is_document_reviewed = (hasEntities && allEntitiesValidated && allRelationsValidated) || isGenerativeTask(taskType);
        data_from_server_post(
            "single-example/update-response",
            {
                client_id: record.client_id,
                dataset: dataset,
                is_benchmark: isBenchmark,
                new_response: newResponse,
                request_example: options.requestExample ?? record.request_example,
                llm_usecase_id: llm_usecase_id,
                is_document_reviewed: is_document_reviewed,
                activity_type: activity_type,
                activity_comment: activity_comment,
                record_id: options.recordId ?? record._id ?? null,
                new_request_example: options.newRequestExample ?? null,
            },
            (result) => {
                if (result?.success) {

                    // After successful update, refresh the data for current page
                    if (updateRecord) {
                        updateRecord(result.new_record, isBenchmark);
                    }

                    // If fetchValidationCounts is provided, call it to update validation counts
                    if (fetchValidationCounts) {
                        fetchValidationCounts();
                    }
                    onDataOverviewRefresh?.();
                    resolve(result);
                } else {
                    sparkToast.error(`Failed to update labels`);
                    reject(new Error('Failed to update labels'));
                }
            }
        );
    });
}
